import { apiFetch } from './api';

export const OFFLINE_QUEUE_KEY = 'eshara_offline_operations_queue_v1';
export const OFFLINE_QUEUE_EVENT = 'eshara-offline-queue-changed';
export const APP_NOTIFICATION_EVENT = 'eshara-app-notification';

export function notifyApp(detail) {
  try {
    window.dispatchEvent(new CustomEvent(APP_NOTIFICATION_EVENT, { detail }));
  } catch {}
}

export function makeOperationId() {
  const random = Math.random().toString(36).slice(2, 10).toUpperCase();
  return `LOCAL-${Date.now()}-${random}`;
}

function safeParse(value, fallback) {
  try {
    return value ? JSON.parse(value) : fallback;
  } catch {
    return fallback;
  }
}

export function getOfflineOperations() {
  try {
    const rows = safeParse(localStorage.getItem(OFFLINE_QUEUE_KEY), []);
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

function saveOfflineOperations(rows) {
  try {
    localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(rows));
    window.dispatchEvent(new CustomEvent(OFFLINE_QUEUE_EVENT, { detail: rows }));
  } catch {}
}

export function subscribeOfflineQueue(callback) {
  const handler = () => callback(getOfflineOperations());
  window.addEventListener(OFFLINE_QUEUE_EVENT, handler);
  window.addEventListener('storage', handler);
  return () => {
    window.removeEventListener(OFFLINE_QUEUE_EVENT, handler);
    window.removeEventListener('storage', handler);
  };
}

export function addOfflineOperation(operation) {
  const rows = getOfflineOperations();
  const id = operation.client_operation_id || makeOperationId();

  if (rows.some((item) => item.client_operation_id === id)) {
    return id;
  }

  const row = {
    id,
    client_operation_id: id,
    endpoint: operation.endpoint,
    method: operation.method || 'POST',
    type: operation.type || 'unknown',
    label: operation.label || 'عملية معلقة',
    payload: {
      ...(operation.payload || {}),
      client_operation_id: id
    },
    created_at: new Date().toISOString(),
    last_error: '',
    status: 'pending',
    attempts: 0
  };

  saveOfflineOperations([row, ...rows]);

  notifyApp({
    type: 'warning',
    title: 'تم حفظ العملية محلياً',
    message: `${row.label} محفوظة كعملية معلقة، ولن تعتمد مالياً إلا بعد رجوع الاتصال وفحص السيرفر.`,
    duration: 8000
  });

  return id;
}

export function removeOfflineOperation(id) {
  const rows = getOfflineOperations().filter((item) => item.id !== id && item.client_operation_id !== id);
  saveOfflineOperations(rows);
}

export function clearCompletedOfflineOperations() {
  const rows = getOfflineOperations().filter((item) => item.status !== 'completed');
  saveOfflineOperations(rows);
}

export function markOfflineOperationFailed(id, errorMessage) {
  const rows = getOfflineOperations().map((item) => {
    if (item.id !== id && item.client_operation_id !== id) return item;

    return {
      ...item,
      status: 'failed',
      last_error: errorMessage || 'فشل إرسال العملية',
      attempts: Number(item.attempts || 0) + 1,
      updated_at: new Date().toISOString()
    };
  });

  saveOfflineOperations(rows);
}

export function restoreOfflineOperationToPending(id) {
  const rows = getOfflineOperations().map((item) => {
    if (item.id !== id && item.client_operation_id !== id) return item;

    return {
      ...item,
      status: 'pending',
      last_error: '',
      updated_at: new Date().toISOString()
    };
  });

  saveOfflineOperations(rows);

  notifyApp({
    type: 'info',
    title: 'إعادة محاولة المزامنة',
    message: 'تمت إعادة العملية إلى حالة الانتظار، وسيحاول النظام مزامنتها عند توفر الاتصال.'
  });
}

async function sendOperation(item) {
  const payload = {
    ...(item.payload || {}),
    client_operation_id: item.client_operation_id || item.id
  };

  const response = await apiFetch(item.endpoint, {
    method: item.method || 'POST',
    body: JSON.stringify(payload)
  });

  const data = response ? await response.json().catch(() => ({})) : {};

  if (!response || !response.ok) {
    const message = data.message || data.error || 'رفض السيرفر العملية بعد فحص الرصيد أو البيانات';
    const error = new Error(message);
    error.serverRejected = !!response;
    error.data = data;
    throw error;
  }

  return data;
}

export async function syncOfflineOperations(options = {}) {
  const rows = getOfflineOperations();
  const pendingRows = rows.filter((item) => item.status === 'pending' || item.status === 'failed');

  if (pendingRows.length === 0) {
    return { synced: 0, failed: 0, total: rows.length, results: [] };
  }

  if (typeof navigator !== 'undefined' && !navigator.onLine) {
    return { synced: 0, failed: pendingRows.length, total: rows.length, offline: true, results: [] };
  }

  const results = [];
  let synced = 0;
  let failed = 0;

  for (const item of pendingRows) {
    try {
      const data = await sendOperation(item);
      synced += 1;
      results.push({ id: item.id, label: item.label, ok: true, data });

      notifyApp({
        type: 'success',
        title: 'تم اعتماد عملية معلقة',
        message: `${item.label} تم إرسالها واعتمادها من السيرفر بعد فحص الرصيد والقواعد.`,
        duration: 9000
      });

      removeOfflineOperation(item.id);
      try {
        window.dispatchEvent(new CustomEvent('eshara-refresh-notifications'));
      } catch {}
    } catch (err) {
      failed += 1;
      const message = err.message || 'فشل إرسال العملية';
      markOfflineOperationFailed(item.id, message);
      results.push({ id: item.id, label: item.label, ok: false, message, serverRejected: err.serverRejected === true });

      notifyApp({
        type: 'error',
        title: 'فشل اعتماد عملية معلقة',
        message: `${item.label}: ${message}`,
        duration: 11000
      });

      if (options.stopOnFirstError) break;
    }
  }

  if (synced > 0 || failed > 0) {
    notifyApp({
      type: failed > 0 ? 'warning' : 'sync',
      title: 'نتيجة مزامنة العمليات',
      message: `تم اعتماد ${synced} عملية، وفشل ${failed} عملية.`,
      duration: 8000
    });
  }

  return { synced, failed, total: getOfflineOperations().length, results };
}
